import { store$ } from '@redux/store';
import { map, filter, distinctUntilChanged, withLatestFrom } from 'rxjs';
import LoadingStatus from 'enums/loadingStatus';
import { selectAppliedFacets, selectFetchResultListingsStatus } from 'features/searchResultsFacets';

/**
 * showNoFiltersHintsSection: when the user sees the no results because of landing in a location where there is
 * nothing to show, meaning: there were no filters applied that lead the search to no results, then the hints
 * section should show a different order on it's hints so the user follow the suggestion in a logical order.
 * The first hint in the section is to clear filters to see more results, but if the user landed in a location
 * where no results, then that hint is placed last and we preffer other hints more valuable for the situation
 * the user is facing.
 */
const showNoFiltersHintsSection = ({ filtersHintsSection, noFiltersHintsSection }) => {
  noFiltersHintsSection.removeAttribute('hidden');
  filtersHintsSection.setAttribute('hidden', '');
};

/**
 * listening to changes on the store. When filters are applied, this observable will emit an array of the
 * current active filters in the search results page.
 */
const onFiltersApplied$ = store$.pipe(map(selectAppliedFacets), distinctUntilChanged());

/**
 * listening to changes on the store. When new results arrived to the search results page, this observable
 * will emit a "Succeeded" status that indicates new data is available for the user.
 */
const onNewResutsShown$ = store$.pipe(
  map(selectFetchResultListingsStatus),
  distinctUntilChanged(),
  filter(
    (fetchResultListingsStatus) =>
      fetchResultListingsStatus === LoadingStatus.Succeeded || fetchResultListingsStatus === LoadingStatus.Idle
  )
);

/**
 * this is a combination of onNewResutsShown$ and onFiltersApplied$ observables. The intention is to determine
 * if the no results page is shown by querying the DOM searching for the clear filters button of this page. If
 * the button is present in the DOM, then we consider the latest filters applied, if there was at least 1 filter
 * applied that means the user ended up in the no results page by applying filters, so we emit a value indicating
 * we have to set up the clear filters button by executing the initClearFilterButton function.
 */
onNewResutsShown$
  .pipe(
    withLatestFrom(onFiltersApplied$),
    map(([, filtersApplied]) => ({
      filtersHintsSection: document.querySelector('[data-filter-applied-hints]'),
      noFiltersHintsSection: document.querySelector('[data-no-filter-applied-hints]'),
      filtersAppliedCount: filtersApplied.length,
    })),
    filter(({ filtersAppliedCount }) => filtersAppliedCount === 0),
    filter(({ filtersHintsSection }) => !!filtersHintsSection),
    filter(({ noFiltersHintsSection }) => !!noFiltersHintsSection)
  )
  .subscribe(showNoFiltersHintsSection);
